import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img19 from "../../../assets/img19.jpg";
import img10 from "../../../assets/img10.jpg";
import { CheckCircle } from "lucide-react";
import BlogSection from "../../../components/BlogSection";

export default function WholeLifeInsurance() {
  return (
    <>
      <SEO
        title="Assurance vie entière Canada | Protection durable – einsured.ca"
        description="Explorez l’assurance-vie entière au Canada avec einsured.ca. Profitez d’une protection à vie, d’une valeur de rachat et d’une sécurité financière."
        keywords="assurance vie entière Canada, assurance vie permanente, assurance vie à valeur de rachat, planification successorale Canada"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20"
        style={{ backgroundImage: `url(${img19})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className="w-full max-w-[1380px] mx-auto">
          <div className="relative z-10 text-white max-w-xl text-center md:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3">
              Assurance vie entière
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-medium mb-6">
              Couverture à vie avec primes garanties, constituez une valeur de rachat et
              laissez un héritage aux générations futures
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-14 sm:py-16 md:py-20 bg-white px-4 sm:px-6 md:px-20">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#49628b]">
              Qu'est-ce que l'assurance vie entière ?
            </h2>

            <p className="mt-6 text-base sm:text-lg leading-8 text-gray-700">
              <span className="text-[#20a7df] font-semibold">
                L'assurance vie entière
              </span>{" "}
              est un type de couverture qui offre une protection permanente et garantit un
              versement non imposable (prestation de décès) à vos proches à votre décès. Le
              meilleur aspect est que vos bénéficiaires peuvent utiliser ce montant comme ils
              le souhaitent, leur offrant la flexibilité financière dont ils ont besoin pendant
              une période émotionnellement difficile.
            </p>

            <p className="mt-6 text-base sm:text-lg leading-8 text-gray-700">
              L'un des principaux avantages de l'assurance vie entière est qu'elle offre une
              protection à vie sans aucun besoin de renouvellement ou d'inquiétude d'expiration.
              En d'autres termes, vous pouvez être assuré que vos proches seront protégés,
              quoi que la vie leur réserve.
            </p>

            <h3 className="mt-10 text-2xl md:text-3xl font-bold text-[#49628b]">
              Connaissez le meilleur aspect de l'assurance vie entière : les valeurs de rachat
            </h3>

            <p className="mt-6 text-base sm:text-lg leading-8 text-gray-700">
              Une autre caractéristique notable de l'assurance vie entière est la{" "}
              <span className="font-semibold text-[#20a7df]">valeur de rachat</span>{" "}
              qui croît à l'intérieur de la police au fil du temps. Ce montant peut être
              utilisé pour des objectifs financiers à long terme comme la planification de la
              retraite ou le remboursement d'une hypothèque, ou pour faire face à des dépenses
              imprévues comme les urgences ou les frais de scolarité. Avec cette police, vos
              proches reçoivent une prestation de décès et vous accumulez également une valeur
              de rachat au fil du temps.
            </p>

            {/* --- Why Whole life insurance --- */}
            <h3 className="mt-14 text-2xl md:text-3xl font-bold text-[#49628b]">
              Pourquoi l'assurance vie entière ?
            </h3>

            <p className="mt-6 text-base sm:text-lg leading-8 text-gray-700">
              Alors que vous construisez une vie avec vos proches, il est crucial de sécuriser
              leur stabilité financière pour toute la durée de votre vie. L'assurance vie
              entière, une option d'assurance durable, offre la tranquillité d'esprit lors de
              divers événements importants de la vie tels que l'achat d'un bien immobilier, la
              fondation d'une famille et le lancement d'une entreprise. En fournissant un
              coussin financier fiable, l'assurance vie entière vous permet de vous concentrer
              sur une vie épanouissante sans vous soucier de laisser vos proches vulnérables
              en période d'incertitude financière.
            </p>

            <p className="mt-6 text-base sm:text-lg leading-8 text-gray-700">
              En résumé, l'{" "}
              <Link to="" className="text-sky-600 font-medium hover:underline">
                assurance vie entière
              </Link>{" "}
              est un placement idéal qui offre une protection permanente à vos proches tout
              en constituant simultanément une valeur de rachat au fil du temps. Cela en fait
              un choix parfait pour ceux qui valorisent à la fois la sécurité et la flexibilité
              dans leur planification financière.
            </p>
          </div>
        </div>
      </section>

      {/* --- Cash Value Accumulation --- */}
      <section
        className="relative py-16 sm:py-20 md:py-28 px-4 sm:px-6 md:px-24 bg-cover bg-center md:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className="max-w-[1380px] mx-auto">
          <div className="relative z-10 max-w-6xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-white">
              Comment la valeur de rachat s'accumule-t-elle dans l'assurance vie entière ?
            </h2>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-8 md:mb-10 text-gray-100">
              L'accumulation de la valeur de rachat dans l'assurance vie entière se produit
              grâce à une combinaison de paiements de primes, d'intérêts crédités à la valeur
              de rachat et de dividendes potentiels.
            </p>

            <h3 className="text-xl sm:text-2xl font-semibold mb-10 md:mb-12 text-sky-200">
              Voici comment cela fonctionne généralement :
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
              <div className="bg-white/95 rounded-2xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Paiements de primes :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Lorsque vous payez des primes pour votre{" "}
                  <span className="font-semibold text-[#49628b]">
                    police d'assurance vie entière
                  </span>
                  , une partie de ces paiements va vers la valeur de rachat de la police.
                  Cette accumulation commence dès le premier paiement de prime et continue
                  tout au long de la vie de la police.
                </p>
              </div>

              <div className="bg-white/95 rounded-2xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Croissance des intérêts :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  La valeur de rachat d'une police d'assurance vie entière croît au fil du
                  temps grâce à l'accumulation d'intérêts. Les compagnies d'assurance
                  garantissent généralement un taux d'intérêt minimum sur la valeur de rachat,
                  assurant qu'elle croît régulièrement tout au long de la vie de la police.
                  Ces intérêts sont généralement crédités à la valeur de rachat sur une base
                  annuelle.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
              <div className="bg-white/95 rounded-2xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Dividendes (le cas échéant) :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Certaines{" "}
                  <span className="font-semibold text-[#49628b]">
                    polices d'assurance vie entière
                  </span>
                  , connues sous le nom de polices participantes, peuvent verser des dividendes
                  aux titulaires. Ces dividendes sont une partie des bénéfices de la compagnie
                  d'assurance et peuvent être utilisés pour améliorer la valeur de rachat.
                  Les titulaires peuvent choisir de recevoir les dividendes en espèces, de les
                  utiliser pour acheter une couverture supplémentaire ou de les laisser
                  s'accumuler dans la valeur de rachat de la police.
                </p>
              </div>

              <div className="bg-white/95 rounded-2xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Avances sur police et retraits :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  Les titulaires de police peuvent accéder à la valeur de rachat de leur
                  assurance vie entière par des avances sur police ou des retraits. Les avances
                  permettent aux titulaires d'emprunter contre la valeur de rachat tout en
                  maintenant la police en vigueur. Les retraits impliquent de prélever une
                  partie de la valeur de rachat, ce qui réduit la prestation de décès et peut
                  être soumis à des frais de rachat et des impôts.
                </p>
              </div>
              <div className="bg-white/95 rounded-2xl p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-2xl font-bold mb-4 text-[#49628b]">
                  Croissance à imposition différée :
                </h4>
                <p className="text-base leading-relaxed text-gray-700">
                  L'un des avantages clés de l'accumulation de la valeur de rachat dans
                  l'assurance vie entière est qu'elle croît à imposition différée. Cela
                  signifie que les titulaires ne paient pas d'impôts sur la croissance de la
                  valeur de rachat tant que la police reste en vigueur. Cependant, les retraits
                  et les avances peuvent être soumis à des impôts et des pénalités potentielles
                  si non gérés correctement.
                </p>
              </div>
            </div>
            <p className="max-w-5xl mx-auto text-base sm:text-lg leading-relaxed mb-4 text-gray-100">
              Dans l'ensemble, la fonction d'accumulation de la valeur de rachat de l'assurance
              vie entière offre aux titulaires une précieuse composante d'épargne et de
              placement, en plus de la protection par prestation de décès.
            </p>

            <p className="max-w-5xl mx-auto text-base sm:text-lg leading-relaxed text-gray-100">
              Elle peut servir de source de flexibilité et de sécurité financières, permettant
              aux titulaires d'accéder à des fonds pour divers besoins tout en maintenant un
              niveau de protection pour leurs proches.
            </p>
          </div>
        </div>
      </section>

      {/* --- TYPES OF WHOLE LIFE INSURANCE --- */}
      <section className="py-16 md:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#49628b] mb-6">
              Types d'assurance vie entière :
            </h2>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-10 md:mb-12">
              Au Canada, comme dans d'autres régions, il existe différents types de{" "}
              <span className="font-semibold text-[#49628b]">
                polices d'assurance vie entière
              </span>{" "}
              qui répondent à différents besoins et préférences financiers. Voici quelques-uns
              des types courants d'assurance vie entière disponibles au Canada :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 text-left">
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière participante :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    ✅ Fournit une prestation de décès garantie avec le potentiel de
                    recevoir des dividendes.
                  </li>
                  <li>
                    ✅ Les dividendes peuvent être utilisés pour améliorer la valeur de
                    rachat, compenser les primes ou être reçus en espèces.
                  </li>
                  <li>
                    ✅ Le titulaire de la police participe à la performance financière de
                    la compagnie d'assurance.
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière non participante :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    ✅ Offre une prestation de décès garantie et une accumulation de valeur
                    de rachat, mais sans le potentiel de dividendes.
                  </li>
                  <li>
                    ✅ Les primes et les prestations sont généralement fixes et garanties.
                  </li>
                </ul>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 text-left">
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière à paiements limités :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    ✅ Similaire à l'{" "}
                    <span className="font-semibold">
                      assurance vie entière traditionnelle
                    </span>{" "}
                    mais avec une période de paiement des primes limitée.
                  </li>
                  <li>
                    ✅ Les primes sont payées pendant un nombre d'années spécifique ou
                    jusqu'à un certain âge, après quoi la police reste en vigueur sans
                    primes supplémentaires.
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière à prime unique :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    ✅ Permet le paiement de la prime entière en un seul versement initial.
                  </li>
                  <li>
                    ✅ Fournit une valeur de rachat immédiate et une prestation de décès
                    garantie.
                  </li>
                </ul>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 text-left">
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière conjointe au dernier décès :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>✅ Assure deux personnes, généralement un couple.</li>
                  <li>
                    ✅ La prestation de décès est versée au décès du dernier assuré
                    survivant.
                  </li>
                  <li>✅ Couramment utilisée à des fins de planification successorale.</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière pour enfants :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    ✅ Fournit une couverture pour un enfant, avec l'option de transférer
                    la propriété à l'enfant une fois qu'il atteint un certain âge.
                  </li>
                  <li>
                    ✅ Peut servir de véhicule d'épargne pour{" "}
                    <a href="" className="text-sky-600 hover:underline">
                      les besoins financiers futurs liés aux études ou autres.
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-8 mb-12 text-left">
              <div className="bg-gray-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition">
                <h4 className="text-2xl font-bold text-[#49628b] mb-4">
                  Assurance vie entière à émission garantie :
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    ✅ Conçue pour les personnes qui pourraient avoir du mal à obtenir une
                    couverture en raison de problèmes de santé.
                  </li>
                  <li>
                    ✅ L'acceptation est généralement garantie sans nécessiter d'examen
                    médical, mais les primes sont plus élevées.
                  </li>
                </ul>
              </div>
            </div>
            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed text-gray-700 mb-10">
              Il est important pour les personnes au Canada d'examiner attentivement les
              caractéristiques, les avantages et les limites de chaque type de{" "}
              <span className="font-semibold text-[#20a7df]">
                police d'assurance vie entière
              </span>{" "}
              avant de prendre une décision.
            </p>

            <a
              href="https://compulife.org/website/655/quoter.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-8 py-3 rounded-full shadow-md transition"
            >
              Obtenez votre meilleur taux
            </a>
          </div>
        </div>
      </section>

      {/* --- PAY PERIODS --- */}
      <section className="relative py-16 md:py-28 px-4 sm:px-6 md:px-24 text-white bg-gradient-to-br from-[#5f83a3] via-[#5ea6c5] to-[#57b0d6]">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Quelles sont les différentes périodes de paiement disponibles dans l'assurance vie entière ?
            </h2>

            <p className="max-w-5xl mx-auto text-base sm:text-lg leading-relaxed mb-10 md:mb-12">
              Au Canada, les polices d'assurance vie entière sont souvent assorties d'options
              de paiement de primes flexibles, et les périodes de paiement limitées font partie
              des choix disponibles. Voici quelques périodes de paiement limitées courantes
              pour l'assurance vie entière au Canada :
            </p>

            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 text-left">
              <div className="rounded-[28px] bg-[#2f5f76]/70 ring-1 ring-white/10 shadow-[0_20px_45px_rgba(0,0,0,0.25)] p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Assurance vie entière à paiements limités – 20 ans :
                </h3>
                <ul className="space-y-4 text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Les primes sont payées pendant une période fixe de{" "}
                      <span className="font-semibold">20 ans</span>.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Après la période de paiement de 20 ans, la police reste en vigueur
                      sans autres paiements de primes requis.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-[28px] bg-[#2f5f76]/70 ring-1 ring-white/10 shadow-[0_20px_45px_rgba(0,0,0,0.25)] p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Assurance vie entière à paiements limités – Jusqu'à 65 ans :
                </h3>
                <ul className="space-y-4 text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Les primes sont payées jusqu'à ce que le titulaire de la police
                      atteigne l'âge de 65 ans.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Une fois que le titulaire atteint 65 ans, il n'est plus tenu
                      d'effectuer des paiements de primes, et la couverture continue.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 text-left">
              <div className="rounded-[28px] bg-[#2f5f76]/70 ring-1 ring-white/10 shadow-[0_20px_45px_rgba(0,0,0,0.25)] p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Assurance vie entière à paiements limités – Jusqu'à 100 ans :
                </h3>
                <ul className="space-y-4 text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Les primes sont payées jusqu'à ce que le titulaire de la police
                      atteigne l'âge de 100 ans.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Après avoir atteint 100 ans, aucun autre paiement de prime n'est
                      requis, et la police reste en vigueur pour la durée de vie de l'assuré.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-[28px] bg-[#2f5f76]/70 ring-1 ring-white/10 shadow-[0_20px_45px_rgba(0,0,0,0.25)] p-8 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Assurance vie entière à prime unique :
                </h3>
                <ul className="space-y-4 text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Le titulaire effectue un seul paiement de prime initial couvrant
                      toute la durée de vie de la police.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-300 mt-1 flex-shrink-0" />
                    <span>
                      Ces options de paiement limitées offrent aux titulaires la flexibilité
                      d'adapter leurs calendriers de paiement de primes selon leurs objectifs
                      et préférences financiers.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="max-w-6xl mx-auto text-base sm:text-lg leading-relaxed mb-6">
              Choisir une période de paiement limitée peut être attrayant pour ceux qui
              souhaitent s'assurer que leur assurance vie est entièrement payée après un
              certain nombre d'années ou à un âge spécifique.
            </p>
            <p className="max-w-6xl mx-auto text-base sm:text-lg leading-relaxed">
              Lors de l'examen d'une police d'assurance vie entière à paiements limités au
              Canada, il est crucial d'examiner attentivement les conditions, les modalités
              et les coûts associés. Consulter un conseiller en assurance agréé chez{" "}
              <a href="/" className="underline text-cyan-200 hover:text-white">
                einsured.ca
              </a>{" "}
              peut aider les individus à sélectionner une période de paiement qui correspond
              à leurs objectifs financiers et assure une couverture à long terme.
            </p>

            {/* CTA */}
            <div className="mt-8 md:mt-10">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto rounded-full bg-cyan-400 px-10 py-4 text-sky-900 font-semibold text-lg shadow-md hover:opacity-90 transition"
              >
                Commencer
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* --- IDEAL AGE TO BUY WHOLE LIFE INSURANCE  --- */}
      <section className="py-16 md:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#49628b] text-center">
              Existe-t-il un âge idéal pour souscrire une assurance vie entière ?
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-center mt-4 mb-6">
              L'âge idéal pour souscrire une assurance vie entière peut varier en fonction
              des circonstances individuelles, des objectifs financiers et des besoins.
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-[#49628b] text-center mb-10">
              Voici quelques considérations pour différents groupes d'âge :
            </h3>
            {(() => {
              const Bullet = ({ children }) => (
                <li className="flex items-start gap-3">
                  <CheckCircle
                    className="h-5 w-5 text-cyan-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="leading-7">{children}</span>
                </li>
              );
              return (
                <>
                  <div className="bg-gray-50 rounded-[22px] ring-1 ring-slate-200 shadow-sm p-6 md:p-8 mb-8">
                    <h4 className="text-xl md:text-2xl font-bold text-[#49628b] mb-4">
                      Jeunes adultes (20 à 30 ans) :
                    </h4>
                    <ul className="space-y-3 text-gray-700 text-base md:text-lg">
                      <Bullet>
                        Souscrire une assurance vie entière à un jeune âge peut être
                        avantageux, car les primes sont généralement moins élevées lorsque la
                        police est souscrite.
                      </Bullet>
                      <Bullet>
                        Bloquer une prime basse à un jeune âge peut offrir des avantages
                        financiers à long terme, surtout si la police est maintenue tout au
                        long de la vie.
                      </Bullet>
                      <Bullet>
                        Les jeunes adultes peuvent également avoir moins de problèmes de
                        santé, ce qui facilite l'obtention d'une couverture.
                      </Bullet>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-[22px] ring-1 ring-slate-200 shadow-sm p-6 md:p-8 mb-8">
                    <h4 className="text-xl md:text-2xl font-bold text-[#49628b] mb-4">
                      Personnes d'âge moyen (40 à 50 ans) :
                    </h4>
                    <ul className="space-y-3 text-gray-700 text-base md:text-lg">
                      <Bullet>
                        Les personnes d'âge moyen peuvent toujours trouver l'{" "}
                        <a href="#" className="text-sky-600 hover:underline">
                          assurance vie entière abordable
                        </a>
                        , mais les primes ont tendance à être plus élevées que lors d'un
                        achat à un âge plus jeune.
                      </Bullet>
                      <Bullet>
                        Ce groupe d'âge peut avoir une compréhension plus claire de ses
                        objectifs financiers à long terme, ce qui en fait une bonne occasion
                        de sécuriser une couverture à des fins de planification successorale
                        ou de remplacement du revenu.
                      </Bullet>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-[22px] ring-1 ring-slate-200 shadow-sm p-6 md:p-8">
                    <h4 className="text-xl md:text-2xl font-bold text-[#49628b] mb-4">
                      Personnes âgées (60 ans et plus) :
                    </h4>
                    <ul className="space-y-3 text-gray-700 text-base md:text-lg">
                      <Bullet>
                        Bien qu'il soit possible de souscrire une assurance vie entière plus
                        tard dans la vie, les primes peuvent être considérablement plus élevées.
                      </Bullet>
                      <Bullet>
                        Les personnes âgées peuvent constater que d'autres formes d'assurance,
                        comme l'{" "}
                        <a
                          href="/fr/assurance-vie-temporaire"
                          className="text-sky-600 hover:underline"
                        >
                          assurance vie temporaire
                        </a>{" "}
                        ou l'assurance vie universelle garantie, répondent mieux à leurs besoins.
                      </Bullet>
                      <Bullet>
                        Les personnes de ce groupe d'âge peuvent donner la priorité à d'autres
                        considérations financières, telles que la planification de la retraite
                        ou les frais de santé.
                      </Bullet>
                    </ul>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </section>

      {/* --- KEY FACTORS --- */}
      <section className="relative bg-[#dbe3ed] border-y-4 border-[#2b4672] py-16 md:py-20 px-4 sm:px-6 md:px-24 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
              Facteurs clés influençant la décision de souscrire une assurance vie entière
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-center mb-12">
              En fin de compte, la décision de souscrire une{" "}
              <span className="text-[#20a7df] font-semibold">
                assurance vie entière
              </span>{" "}
              doit être basée sur la situation financière spécifique, les objectifs et la
              tolérance au risque d'un individu.
            </p>

            <div className="space-y-6 text-gray-700 text-base md:text-lg leading-relaxed">
              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-5 w-5 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Objectifs financiers :
                  </span>{" "}
                  L'assurance vie entière peut être bénéfique pour la planification
                  successorale, fournissant une prestation de décès garantie et une
                  accumulation potentielle de valeur de rachat. Si laisser un héritage ou
                  assurer la protection financière des proches est une priorité, l'assurance
                  vie entière peut être appropriée.
                </p>
              </div>

              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-5 w-5 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Accessibilité financière :
                  </span>{" "}
                  Les primes de l'assurance vie entière sont généralement plus élevées que
                  celles de l'assurance vie temporaire. Les individus doivent évaluer leur
                  budget et déterminer si les primes continues s'intègrent confortablement
                  dans leur plan financier.
                </p>
              </div>

              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-5 w-5 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    État de santé :
                  </span>{" "}
                  En général, il est plus facile d'être admissible à une{" "}
                  <a href="" className="text-sky-600 hover:underline">
                    assurance vie
                  </a>{" "}
                  lorsqu'on est en bonne santé. Les personnes ayant des problèmes de santé
                  peuvent faire face à des primes plus élevées ou à des défis potentiels
                  pour obtenir une couverture.
                </p>
              </div>

              <div className="flex gap-3 items-start">
                <CheckCircle
                  className="h-5 w-5 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Engagement à long terme :
                  </span>{" "}
                  L'{" "}
                  <a href="#" className="text-sky-600 hover:underline">
                    assurance vie entière
                  </a>{" "}
                  est un engagement à long terme. Si quelqu'un cherche une couverture
                  temporaire ou a des objectifs financiers à plus court terme, d'autres types
                  d'assurance, comme l'assurance vie temporaire, pourraient être plus
                  appropriés.
                </p>
              </div>
            </div>
            <p className="mt-12 text-base md:text-lg text-gray-700 leading-relaxed max-w-5xl mx-auto text-center">
              Il est conseillé de consulter un conseiller en assurance agréé pour évaluer
              les besoins individuels et déterminer le type et le moment les plus appropriés
              pour la couverture d'assurance vie.
            </p>

            {/* CTA button */}
            <div className="flex justify-center mt-8 md:mt-10">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-[#00b8f1] text-sky-900 font-semibold text-base md:text-lg px-8 py-4 rounded-full shadow-md hover:opacity-90 transition text-center"
              >
                Planifiez une consultation gratuite
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
